(() => {
  if (customElements.get('eva-landing')) return;

  class EvaLanding extends HTMLElement {
    connectedCallback() {
      this.cleanup();

      this.evaAbort = new AbortController();
      this.evaTimers = new Map();

      const signal = this.evaAbort.signal;
      const tabs = [...this.querySelectorAll('[data-eva-tab]')];
      const panels = [...this.querySelectorAll('[data-eva-panel]')];
      const sticky = this.querySelector('[data-eva-sticky]');
      const status = this.querySelector('[data-eva-status]');

      const selectTab = (key) => {
        if (!panels.some((panel) => panel.dataset.evaPanel === key)) {
          return;
        }

        panels.forEach((panel) => {
          panel.hidden = panel.dataset.evaPanel !== key;
        });

        tabs.forEach((tab) => {
          const active = tab.dataset.evaTab === key;

          tab.classList.toggle('is-active', active);
          tab.setAttribute('aria-selected', String(active));
          tab.tabIndex = active ? 0 : -1;
        });
      };

      const updateStickyHeight = () => {
        const height =
          sticky && !sticky.hidden
            ? Math.ceil(sticky.getBoundingClientRect().height)
            : 0;

        this.style.setProperty('--eva-sticky-height', `${height}px`);
      };

      this.classList.add('is-ready');

      this.querySelectorAll('[data-eva-copy]').forEach((button) => {
        button.hidden = false;
      });

      this.querySelectorAll('[data-eva-close]').forEach((button) => {
        button.hidden = false;
      });

      this.querySelector('[data-eva-tabs]')?.setAttribute('role', 'tablist');

      tabs.forEach((tab) => {
        tab.setAttribute('role', 'tab');
      });

      panels.forEach((panel) => {
        panel.setAttribute('role', 'tabpanel');
      });

      selectTab('all');

      this.addEventListener(
        'click',
        async (event) => {
          if (!(event.target instanceof Element)) return;

          const tab = event.target.closest('[data-eva-tab]');

          if (tab && this.contains(tab)) {
            event.preventDefault();
            selectTab(tab.dataset.evaTab);
            return;
          }

          const close = event.target.closest('[data-eva-close]');

          if (close && sticky) {
            sticky.hidden = true;
            updateStickyHeight();

            const activeTab = tabs.find(
              (item) => item.getAttribute('aria-selected') === 'true'
            );

            activeTab?.focus({ preventScroll: true });
            return;
          }

          const copy = event.target.closest('[data-eva-copy]');

          if (!copy || copy.disabled) return;

          const label = copy.querySelector('[data-eva-copy-label]');
          const originalLabel = label?.textContent;
          const code = copy.dataset.evaCopy || 'EVA';

          copy.disabled = true;

          if (status) status.textContent = '';

          try {
            await navigator.clipboard.writeText(code);

            if (signal.aborted) return;

            const message = this.dataset.evaCopied || 'Gekopieerd!';

            if (label) label.textContent = message;
            if (status) status.textContent = message;

            const restore = () => {
              if (label) label.textContent = originalLabel;
              copy.disabled = false;
            };

            const timer = window.setTimeout(() => {
              restore();
              this.evaTimers.delete(copy);
            }, 1800);

            this.evaTimers.set(copy, { timer, restore });
          } catch {
            if (signal.aborted) return;

            copy.disabled = false;

            const message =
              this.dataset.evaCopyError ||
              'Kopiëren lukt niet. Kopieer deze code handmatig:';

            if (status) status.textContent = `${message} ${code}`;

            window.prompt(message, code);
          } finally {
            if (signal.aborted) copy.disabled = false;
          }
        },
        { signal }
      );

      this.addEventListener(
        'keydown',
        (event) => {
          if (!(event.target instanceof Element)) return;

          const tab = event.target.closest('[data-eva-tab]');
          const index = tabs.indexOf(tab);

          if (index < 0) return;

          let nextIndex;

          switch (event.key) {
            case 'ArrowRight':
              nextIndex = (index + 1) % tabs.length;
              break;
            case 'ArrowLeft':
              nextIndex = (index - 1 + tabs.length) % tabs.length;
              break;
            case 'Home':
              nextIndex = 0;
              break;
            case 'End':
              nextIndex = tabs.length - 1;
              break;
            default:
              return;
          }

          event.preventDefault();
          selectTab(tabs[nextIndex].dataset.evaTab);
          tabs[nextIndex].focus();
        },
        { signal }
      );

      document.addEventListener(
        'shopify:block:select',
        (event) => {
          if (!(event.target instanceof Element)) return;
          if (!this.contains(event.target)) return;

          const blockId = String(event.detail?.blockId || '');

          const tab =
            tabs.find((item) => item.dataset.evaTab === blockId) ||
            event.target.closest('[data-eva-tab]') ||
            event.target.querySelector('[data-eva-tab]');

          if (tab) selectTab(tab.dataset.evaTab);
        },
        { signal }
      );

      if (sticky && 'ResizeObserver' in window) {
        this.evaObserver = new ResizeObserver(updateStickyHeight);
        this.evaObserver.observe(sticky);
      }

      window.addEventListener('resize', updateStickyHeight, { signal });
      updateStickyHeight();
    }

    disconnectedCallback() {
      this.cleanup();
    }

    cleanup() {
      this.evaAbort?.abort();
      this.evaObserver?.disconnect();

      this.evaTimers?.forEach(({ timer, restore }) => {
        window.clearTimeout(timer);
        restore();
      });

      this.evaTimers?.clear();
    }
  }

  customElements.define('eva-landing', EvaLanding);
})();